import type { PlanEntitlements, PlanUpgradePayload } from "@/api/fair-use"

// Plan-entitlement helpers for the fair-use user surface (round 2, #3087).
// Split out of fair-use-utils.ts so neither file carries two subjects: that one
// is about the MONTH'S BUDGET (percent, spend, the block/downgrade states),
// this one is about WHAT THE PLAN INCLUDES. Nothing here imports from there,
// so `fair-use-utils` can depend on this module's code constant without a
// cycle.

/** The stable `code`/`error` string every plan refusal carries. */
export const PLAN_UPGRADE_REQUIRED_CODE = "plan_upgrade_required"

/**
 * Feature ids the backend puts on `plan_upgrade_payload.feature`, mapped to
 * the i18n leaf under `fairUse.features.*`.
 *
 * A feature that is NOT listed still renders — {@link planFeatureLabelKey}
 * falls back to a generic label — because a newer backend must be able to gate
 * something this build has never heard of without the card going blank.
 */
const PLAN_FEATURE_LABEL_KEYS: Record<string, string> = {
  deep: "fairUse.features.deep",
  voice: "fairUse.features.voice",
  model: "fairUse.features.model",
  images: "fairUse.features.images",
  scheduled_flows: "fairUse.features.scheduledFlows",
  flow_max_steps: "fairUse.features.flowSteps",
  flow_max_seconds: "fairUse.features.flowSeconds",
}

/** i18n key for a refused feature id, or the generic fallback. */
export function planFeatureLabelKey(feature: string | undefined): string {
  if (!feature) return "fairUse.features.generic"
  return PLAN_FEATURE_LABEL_KEYS[feature] ?? "fairUse.features.generic"
}

function readPositiveInt(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined
}

/**
 * Read a `plan_upgrade_required` payload off a WS error frame or an HTTP error
 * body, whichever envelope it arrived in.
 *
 * Three shapes are in the wild and all three are the same refusal:
 * - Explorer WS: `{type:"error", data:{error:"plan_upgrade_required", ...}}`
 * - realtime (voice) WS: FLAT — that transport has no `data` envelope
 * - HTTP 400/403 bodies: the payload itself
 *
 * Returns null for anything else. `feature` is required (it is what the card
 * names); everything else is optional and only carried through when it has the
 * right type, so a malformed frame degrades to a generic upgrade notice
 * instead of rendering `undefined` at the user.
 */
export function extractPlanUpgradePayload(source: unknown): PlanUpgradePayload | null {
  if (!source || typeof source !== "object") return null
  const record = source as Record<string, unknown>
  const candidates: unknown[] = [record, record.data, (record as { body?: unknown }).body]
  for (const candidate of candidates) {
    if (!candidate || typeof candidate !== "object") continue
    const entry = candidate as Record<string, unknown>
    if (entry.error !== PLAN_UPGRADE_REQUIRED_CODE && entry.code !== PLAN_UPGRADE_REQUIRED_CODE) {
      continue
    }
    const payload: PlanUpgradePayload = {
      error: PLAN_UPGRADE_REQUIRED_CODE,
      code: PLAN_UPGRADE_REQUIRED_CODE,
      feature: typeof entry.feature === "string" && entry.feature ? entry.feature : "",
    }
    if (typeof entry.plan === "string" && entry.plan) payload.plan = entry.plan
    if (Array.isArray(entry.required_plans)) {
      const plans = entry.required_plans.filter((plan): plan is string => typeof plan === "string")
      if (plans.length > 0) payload.required_plans = plans
    }
    const limit = readPositiveInt(entry.limit)
    if (limit !== undefined) payload.limit = limit
    const used = readPositiveInt(entry.used)
    if (used !== undefined) payload.used = used
    if (typeof entry.message === "string" && entry.message) payload.message = entry.message
    return payload
  }
  return null
}

/**
 * Read a plan refusal off a REJECTED HTTP call.
 *
 * The REST refusals (the scheduled-flow cap on
 * `POST /api/explorer/workflow/<id>/schedule`) arrive as a rejected axios
 * error, whose body sits on `response.data` — one level deeper than the WS
 * frames {@link extractPlanUpgradePayload} was written for. Duck-typed rather
 * than `axios.isAxiosError`, so a caller that already unwrapped the body, or a
 * fetch-shaped error, is read the same way and the module stays free of an
 * axios import.
 *
 * Returns null for every other failure, which is what keeps an ordinary error
 * on its ordinary path (a toast) instead of dressing it up as a plan refusal.
 */
export function extractPlanUpgradeFromError(error: unknown): PlanUpgradePayload | null {
  if (!error || typeof error !== "object") return null
  const body = (error as { response?: { data?: unknown } }).response?.data
  return extractPlanUpgradePayload(body) ?? extractPlanUpgradePayload(error)
}

/** One plan-locked capability, as the popover lists it. */
export type LockedPlanFeature = {
  /** Backend feature id — also the i18n lookup through {@link planFeatureLabelKey}. */
  feature: string
  /**
   * The counted ceiling that locks it (0 for `scheduled_flows`/`images`), or
   * undefined for the two booleans, which are simply absent from the plan.
   */
  limit?: number
}

/**
 * What this plan does NOT include, in the order the popover lists it.
 *
 * Only hard locks appear: a boolean that is false, and a count that is exactly
 * 0. A non-zero count is a budget the user still has (the flow/image caps
 * refuse with their own `limit`/`used` payload when it runs out), and `null`
 * means no limit at all — neither belongs on a "locked" list.
 */
export function lockedPlanFeatures(
  entitlements: PlanEntitlements | null | undefined
): LockedPlanFeature[] {
  if (!entitlements) return []
  const locked: LockedPlanFeature[] = []
  if (entitlements.deep === false) locked.push({ feature: "deep" })
  if (entitlements.voice === false) locked.push({ feature: "voice" })
  if (entitlements.scheduled_flows === 0) locked.push({ feature: "scheduled_flows", limit: 0 })
  if (entitlements.images_per_month === 0) locked.push({ feature: "images", limit: 0 })
  return locked
}
